import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Subscription, combineLatest, firstValueFrom } from 'rxjs';
import { HelperFunctionsService } from 'projects/tools/src/lib/shared-module/helpers/helper-functions.service';
import { PuzzleAnalyticsService } from 'projects/tools/src/lib/shared-module/puzzle-analytics/puzzle-analytics.service';
import { SetConsent } from 'projects/tools/src/lib/store/puzzle-analytics-consent.actions';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { LeaderboardService } from 'projects/tools/src/lib/shared-module/leaderboard/leaderboard.service';
import {
  AlertController,
  ModalController,
  IonButton,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonIcon,
  IonItem,
  IonList,
  IonListHeader,
  IonRow,
  IonText,
  IonToggle,
  IonRadioGroup,
  IonRadio,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { informationCircleSharp, playSharp, bulbOutline } from 'ionicons/icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { TooltipModule } from 'ng2-tooltip-directive-major-angular-updates';

import { PuzzleCompatibillityWarningComponent } from 'projects/tools/src/lib/shared-module/puzzle-compatibillity-warning/puzzle-compatibillity-warning.component';
import { PuzzleDesktopModeWarningComponent } from 'projects/tools/src/lib/shared-module/puzzle-desktop-mode-warning/puzzle-desktop-mode-warning.component';
import { PuzzleLocalstorageWarningComponent } from 'projects/tools/src/lib/shared-module/puzzle-localstorage-warning/puzzle-localstorage-warning.component';
import { PuzzleChangerFormComponent } from 'projects/tools/src/lib/shared-module/puzzle-changer-form/puzzle-changer-form.component';
import { SafeResourceUrl } from '@angular/platform-browser';
import { PuzzleUpdateNotificationsModel } from '../../store/puzzle-update-notifications.model';
import { PuzzleUpdateNotificationModalComponent } from '../puzzle-update-notification-modal/puzzle-update-notification-modal.component';

@Component({
  selector: 'lib-app-puzzle-welcome-dialog',
  templateUrl: './puzzle-welcome-dialog.component.html',
  styleUrls: ['./puzzle-welcome-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    TooltipModule,
    IonButton,
    IonCheckbox,
    IonCol,
    IonContent,
    IonFooter,
    IonGrid,
    IonIcon,
    IonItem,
    IonList,
    IonListHeader,
    IonRow,
    IonText,
    IonToggle,
    PuzzleCompatibillityWarningComponent,
    PuzzleDesktopModeWarningComponent,
    PuzzleLocalstorageWarningComponent,
    PuzzleChangerFormComponent,
    IonRadioGroup,
    IonRadio,
  ],
})
export class PuzzleWelcomeDialogComponent {
  /**
   * When set, the default dialog title will be overwritten
   */
  @Input() title: string;

  /**
   * When set, the default dialog message will be overwritten
   */
  @Input() text: string;
  @Input() started: boolean;

  public compatibilityWarning = false;
  public consent = true;
  public requestConsent = false;
  public strictRulesActive = false;
  public wantsLeaderboard = false;
  public leaderboardSubmissionEndLocalizedStr = '';
  public withinLeaderboardSubmissionTime = false;
  public puzzleIsFinished = false;
  private consentSubscription: Subscription;
  private latestSubscription: Subscription;
  public noLocalStorage = false;
  public showDesktopModeWarning = false;
  private leaderboardTooltipMessage: string;
  public leaderboardDialogMessage: string;

  public apply: () => void;

  public privacyUrl: SafeResourceUrl;

  public updateNotifications: PuzzleUpdateNotificationsModel;

  constructor(
    // public dialogRef: MatDialogRef<PuzzleWelcomeDialogComponent>,
    protected helperFunctions: HelperFunctionsService,
    public analytics: PuzzleAnalyticsService,
    public store: Store,
    public framework: PuzzleFrameworkService,
    private ionicModal: ModalController,
    private ionicAlert: AlertController,
    private translate: TranslateService,
    public device: DeviceDetectorService,
    public leaderboard: LeaderboardService // @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    addIcons({
      informationCircleSharp,
      playSharp,
      bulbOutline,
    });

    // Prepare the privacy URL using the framework service
    this.privacyUrl = this.framework.preparePrivacyUrl(
      this.framework.puzzleFrameworkConfig.privacyPolicy,
      this.analytics.enabled
    );

    this.noLocalStorage = !helperFunctions.localStorageAvailable();

    this.compatibilityWarning = this.helperFunctions.unsupportedBrowser(
      this.framework.hasKeyboardInput,
      this.framework.puzzleFrameworkConfig.nativeKeyboard
    );

    this.showDesktopModeWarning = helperFunctions.isMobileDesktopMode();

    this.consentSubscription = this.store
      .select((state) => state.cookieConsent)
      .subscribe((consent) => {
        this.consent = consent; // save consent for template
        if (!this.requestConsent) {
          // can only be set to true
          this.requestConsent = !consent;
        }
      });
    this.latestSubscription = combineLatest(
      this.leaderboard.strictRulesActive$,
      this.leaderboard.leaderboardSubmissionEndLocalizedStr$,
      this.leaderboard.withinLeaderboardSubmissionTime$,
      this.leaderboard.puzzleIsFinished$
    ).subscribe(
      async ([strictRulesActive, submissionEndLocalized, withinLeaderboardSubmissionTime, puzzleIsFinished]) => {
        this.strictRulesActive = strictRulesActive as boolean;
        this.leaderboardSubmissionEndLocalizedStr = submissionEndLocalized as string;
        this.withinLeaderboardSubmissionTime = withinLeaderboardSubmissionTime as boolean;
        this.puzzleIsFinished = puzzleIsFinished as boolean;

        // If we load the strict rules, we want to participate in the leaderboard
        if (this.strictRulesActive) {
          this.wantsLeaderboard = true;
        }

        // if user already submitted, that beats all other messages
        if (this.leaderboard.leaderboardAssociation?.submittedEntryUUID) {
          this.leaderboardTooltipMessage = await this.translate
            .get('PUZZLE.welcome_dialog.leaderboard.message_submitted', {
              submissionEndLocalized: this.leaderboardSubmissionEndLocalizedStr,
            })
            .toPromise();
          this.leaderboardDialogMessage = await this.translate
            .get('PUZZLE.welcome_dialog.leaderboard.message_submitted_short', {
              submissionEndLocalized: this.leaderboardSubmissionEndLocalizedStr,
            })
            .toPromise();
        } else if (!this.withinLeaderboardSubmissionTime) {
          // We are within the submission time
          this.leaderboardTooltipMessage = await this.translate
            .get('PUZZLE.welcome_dialog.leaderboard.message_old', {
              submissionEndLocalized: this.leaderboardSubmissionEndLocalizedStr,
            })
            .toPromise();
          this.leaderboardDialogMessage = await this.translate
            .get('PUZZLE.welcome_dialog.leaderboard.message_old_short', {
              submissionEndLocalized: this.leaderboardSubmissionEndLocalizedStr,
            })
            .toPromise();
          // We are within the submission time
        } else if (
          this.framework.puzzleFrameworkConfig.leaderboardSubmissionPolicy === 'first-only' &&
          this.leaderboard.leaderboardAssociation?.startCounter >= 1
        ) {
          // And have already played the puzzle once or more
          this.leaderboardTooltipMessage = await this.translate
            .get('PUZZLE.welcome_dialog.leaderboard.message_current_first_only', {
              submissionEndLocalized: this.leaderboardSubmissionEndLocalizedStr,
            })
            .toPromise();
          this.leaderboardDialogMessage = await this.translate
            .get('PUZZLE.welcome_dialog.leaderboard.message_current_first_only_short', {
              submissionEndLocalized: this.leaderboardSubmissionEndLocalizedStr,
            })
            .toPromise();
        } else {
          // And haven't finished the puzzle
          this.leaderboardTooltipMessage = await this.translate
            .get('PUZZLE.welcome_dialog.leaderboard.message_current_unsolved', {
              submissionEndLocalized: this.leaderboardSubmissionEndLocalizedStr,
            })
            .toPromise();
          this.leaderboardDialogMessage = await this.translate
            .get('PUZZLE.welcome_dialog.leaderboard.message_current_unsolved_short', {
              submissionEndLocalized: this.leaderboardSubmissionEndLocalizedStr,
            })
            .toPromise();
        }
      }
    );

    this.store
      .select((state) => state.puzzleUpdateNotifications)
      .subscribe((n) => {
        this.updateNotifications = n;
      });
  }

  want_leaderboard() {
    // If we do not want to participate in the leaderboard, we secretly still do but in silver league.
    if (!this.wantsLeaderboard) {
      this.strictRulesActive = false;
    }
  }

  async closeDialog() {
    this.latestSubscription.unsubscribe();
    if (this.consent) {
      await this.store.dispatch(new SetConsent());
      await this.leaderboard.initLeaderboardGame(this.strictRulesActive);
      await this.ionicModal.dismiss();
      if (this.analytics.enabled) {
        this.consentSubscription.unsubscribe();
      }
    }
    this.leaderboard.checkInitialSettings();
  }

  showPrivacyDialog(event: Event) {
    event.preventDefault();
    if (event.stopPropagation) {
      event.stopPropagation();
    }
    this.analytics.openPrivacyDialog(false);
  }

  async showUpdateNotificationDialog(event: Event) {
    const modal = await this.ionicModal.create({
      component: PuzzleUpdateNotificationModalComponent,
      backdropDismiss: true,
      cssClass: 'puzzle-modal',
    });

    await modal.present();
    await modal.onDidDismiss();
    event.preventDefault();
  }

  async applyButtonCallback() {
    if (this.consent) {
      await this.store.dispatch(new SetConsent());
    }
    if (typeof this.apply === 'function') {
      this.apply();
    }
  }

  saveApplyCallback(applyCallback: () => void) {
    this.apply = applyCallback;
  }

  async showPrivacyTooltip(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    let message = await firstValueFrom(
      this.translate.get('PUZZLE.welcome_dialog.analytics_consent_message.tooltip_without_analytics')
    );
    if (this.analytics.enabled) {
      message = await firstValueFrom(
        this.translate.get('PUZZLE.welcome_dialog.analytics_consent_message.tooltip_with_analytics')
      );
    }
    const button = await firstValueFrom(this.translate.get('ANALYTICS.privacy_dialog.close_button'));

    const alert = await this.ionicAlert.create({
      message,
      backdropDismiss: true,
      cssClass: 'puzzle-alert',
      buttons: [
        {
          text: button,
          role: 'cancel',
          cssClass: 'primary',
        },
      ],
    });

    await alert.present();
  }

  async showLeaderboardTooltip(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    let message = await firstValueFrom(
      this.translate.get('PUZZLE.welcome_dialog.leaderboard.tooltip_' + (this.strictRulesActive ? 'strict' : 'relaxed'))
    );
    message += '<br/><br/>';
    // Extend the message with some submission information:
    message += this.leaderboardTooltipMessage;

    const button = await this.translate.get('ANALYTICS.privacy_dialog.close_button').toPromise();

    const alert = await this.ionicAlert.create({
      message,
      backdropDismiss: true,
      cssClass: 'puzzle-alert leaderboard-alert',
      buttons: [
        {
          text: button,
          role: 'cancel',
          cssClass: 'primary',
        },
      ],
    });

    await alert.present();
  }
}
